import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { hasPermission } from "@/lib/permissions";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const REGIONS = [
  { value: "centro", label: "Zona Centro" },
  { value: "norte", label: "Zona Norte" },
  { value: "bajio", label: "Zona Bajío" },
  { value: "otros", label: "Otras zonas" },
];
const REGION_LABEL = Object.fromEntries(REGIONS.map((r) => [r.value, r.label]));
const th = "px-3 py-2 text-left text-[10px] uppercase tracking-[0.12em] text-[#5C6A7D] font-semibold whitespace-nowrap";
const td = "px-3 py-3 text-sm text-[#0A2540] align-middle";

export default function AdminProjects() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState({ name: "", region: "", location: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/projects").then(({ data }) => setRows(data));
  useEffect(() => { if (hasPermission(user, "manage_projects")) load(); }, []);

  if (user && !hasPermission(user, "manage_projects")) return <Navigate to="/" replace />;

  const add = async () => {
    if (!draft.name.trim()) { toast.error("El nombre del proyecto es obligatorio"); return; }
    if (!draft.region) { toast.error("Selecciona la zona"); return; }
    setBusy(true);
    try {
      await api.post("/projects", draft);
      setDraft({ name: "", region: "", location: "" });
      toast.success("Proyecto agregado");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo agregar"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/projects/${id}`); toast.success("Proyecto borrado"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo borrar"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-projects-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C6A7D]">Administración</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#0A2540] tracking-tighter mt-1">Control de proyectos.</h1>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-sm overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead className="bg-[#FAFAFA] border-b border-[#E2E8F0]">
            <tr><th className={th}>Nombre</th><th className={th}>Compañía</th><th className={th}>Zona</th><th className={th}>Ubicación</th><th className={th}>Acción</th></tr>
          </thead>
          <tbody className="divide-y divide-[#E2E8F0]">
            {rows.map((p) => (
              <tr key={p.id} data-testid={`project-row-${p.id}`}>
                <td className={td}><span className="font-semibold">{p.name}</span></td>
                <td className={td} data-testid={`project-company-${p.id}`}>{p.company_name || "—"}</td>
                <td className={td}>{REGION_LABEL[p.region] || "—"}</td>
                <td className={td}>{p.location || "—"}</td>
                <td className={td}>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button className="inline-flex items-center gap-1 text-xs text-[#9F1239] hover:underline" data-testid={`delete-project-${p.id}`}><Trash2 size={13} /> Borrar</button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>¿Borrar el proyecto "{p.name}"?</AlertDialogTitle>
                        <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(p.id)} className="bg-[#9F1239] hover:bg-[#881337]" data-testid={`confirm-delete-project-${p.id}`}>Borrar</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </td>
              </tr>
            ))}
            {/* Fila para agregar proyecto */}
            <tr className="bg-[#FAFAFA]/60" data-testid="project-new-row">
              <td className="px-3 py-2"><Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Nombre" className="h-9 rounded-sm" data-testid="new-project-name" /></td>
              <td className="px-3 py-2 text-xs text-[#5C6A7D]">—</td>
              <td className="px-3 py-2">
                <Select value={draft.region} onValueChange={(v) => setDraft({ ...draft, region: v })}>
                  <SelectTrigger className="h-9 rounded-sm min-w-[150px]" data-testid="new-project-region"><SelectValue placeholder="Zona" /></SelectTrigger>
                  <SelectContent>{REGIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </td>
              <td className="px-3 py-2"><Input value={draft.location} onChange={(e) => setDraft({ ...draft, location: e.target.value })} placeholder="Ubicación" className="h-9 rounded-sm" data-testid="new-project-location" /></td>
              <td className="px-3 py-2"><Button onClick={add} disabled={busy} variant="outline" className="h-9 rounded-sm" data-testid="add-project-btn"><Plus size={14} className="mr-1" /> Agregar</Button></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
